import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp, MessageCircle, Mail } from "lucide-react";
import axios from "axios";

// ✅ Unified base URL from .env
const API_BASE = process.env.REACT_APP_API_BASE;

export default function Help() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-100 via-white to-neutral-50 text-neutral-900">
      {/* =================== HEADER =================== */}
      <div className="text-center py-16">
        <h1 className="text-5xl font-extrabold bg-gradient-to-r from-neutral-900 to-neutral-600 bg-clip-text text-transparent mb-4">
          Help & Support
        </h1>
        <p className="text-neutral-600 max-w-xl mx-auto text-lg">
          Quick answers, live chat, or email — we’re here to help you every step of the way.
        </p>
      </div>

      {/* =================== FAQ SECTION =================== */}
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-3xl font-bold text-center mb-10">Frequently Asked Questions</h2>
        <FAQSection />
      </div>

      {/* =================== LIVE CHAT SECTION =================== */}
      <div className="max-w-5xl mx-auto mt-20 px-6">
        <div className="bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-3xl shadow-2xl p-10 text-white flex flex-col sm:flex-row justify-between items-center gap-8">
          <div className="text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-semibold mb-2">Need Live Support?</h2>
            <p className="text-neutral-300">
              Our agents are available Monday–Friday,{" "}
              <span className="font-semibold text-white">9 AM – 5 PM (Pacific Time)</span>
            </p>
          </div>

          <button
            onClick={() => navigate("/live-chat")}
            className="flex items-center gap-2 px-8 py-3 bg-white text-neutral-900 font-semibold rounded-2xl hover:opacity-90 transition-all shadow-md hover:shadow-lg"
          >
            <MessageCircle className="w-5 h-5" />
            Start Live Chat
          </button>
        </div>
      </div>

      {/* =================== EMAIL SUPPORT (Ticket Form) =================== */}
      <TicketEmailForm />

      {/* =================== FOOTER =================== */}
      <div className="border-t bg-neutral-50 py-6 text-center text-sm text-neutral-500">
        © {new Date().getFullYear()} Book Your Need — All rights reserved.
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 🎟️ Support Ticket Email Form                                              */
/* -------------------------------------------------------------------------- */
function TicketEmailForm() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const userEmail = localStorage.getItem("userEmail");
  const userName = localStorage.getItem("userName");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) return alert("Please fill out all fields.");
    setLoading(true);

    try {
      await axios.post(`${API_BASE}/api/support/send-ticket-email`, {
        subject,
        message,
        userEmail,
        userName,
      });
      setSent(true);
      setSubject("");
      setMessage("");
    } catch (err) {
      console.error("❌ Ticket send failed:", err);
      alert("Failed to send ticket. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="text-center py-20 px-6">
        <Mail className="w-12 h-12 text-green-600 mx-auto mb-3" />
        <h3 className="text-2xl font-semibold text-green-600 mb-2">
          Ticket Submitted Successfully
        </h3>
        <p className="text-neutral-600">
          Our support team will reach out to you shortly at <b>{userEmail}</b>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto text-center py-24 px-6">
      <h2 className="text-3xl font-bold mb-4">Need to Send Us a Ticket?</h2>
      <p className="text-neutral-600 mb-8 max-w-lg mx-auto">
        Fill out the form below and our team will get back to you via email within 24 hours.
      </p>

      <form
        onSubmit={handleSubmit}
        className="backdrop-blur-lg bg-white/60 border border-neutral-200 shadow-lg rounded-3xl p-8 transition hover:shadow-xl"
      >
        <input
          type="text"
          placeholder="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full mb-4 border border-neutral-300 rounded-xl p-3 focus:ring-2 focus:ring-neutral-900 focus:outline-none"
        />
        <textarea
          rows="4"
          placeholder="Describe your issue..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="w-full mb-6 border border-neutral-300 rounded-xl p-3 focus:ring-2 focus:ring-neutral-900 focus:outline-none"
        />
        <button
          type="submit"
          disabled={loading}
          className="w-full bg-neutral-900 text-white py-3 rounded-2xl text-lg font-medium hover:opacity-90 transition"
        >
          {loading ? "Sending..." : "Submit Ticket"}
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 🧠 FAQ Section — glassy 2025 version                                       */
/* -------------------------------------------------------------------------- */
function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      q: "How can I contact customer support?",
      a: "You can chat with us live during support hours or email us anytime at donotreply@bookyourneed.com. We respond to all tickets within 24 hours.",
    },
    {
      q: "When is your support available?",
      a: "Our support team is available Monday to Friday, from 9 AM to 5 PM (Pacific Time). You can still email us after hours and we’ll respond next business day.",
    },
    {
      q: "Can I cancel or reschedule a job?",
      a: "Yes! If no worker has accepted the job yet, you can cancel directly in the app. Once accepted, please contact support to assist you.",
    },
    {
      q: "Are my payments safe?",
      a: "Yes — BYN uses secure payment processing. Funds are held until the job is completed and confirmed by you before being released to the worker.",
    },
    {
      q: "How are workers verified?",
      a: "Every worker goes through identity verification, background checks, and certification validation before joining our platform.",
    },
  ];

  return (
    <div className="grid sm:grid-cols-2 gap-6">
      {faqs.map((item, i) => (
        <div
          key={i}
          className={`relative rounded-3xl p-6 sm:p-8 transition-all duration-300 cursor-pointer border ${
            openIndex === i
              ? "backdrop-blur-xl bg-white/70 border-neutral-300 shadow-2xl"
              : "backdrop-blur-md bg-white/40 border-neutral-200 hover:bg-white/60 hover:shadow-lg"
          }`}
          onClick={() => setOpenIndex(openIndex === i ? null : i)}
        >
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg sm:text-xl font-semibold text-neutral-800 pr-4">
              {item.q}
            </h3>
            {openIndex === i ? (
              <ChevronUp className="w-5 h-5 text-neutral-500" />
            ) : (
              <ChevronDown className="w-5 h-5 text-neutral-500" />
            )}
          </div>
          <div
            className={`overflow-hidden transition-all duration-500 ${
              openIndex === i ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
            }`}
          >
            <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
              {item.a}
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
